import { CancelToken } from 'axios';

import { isFalsy, isTruthy } from 'lib/Helpers';

export const tableStateTemplate = {
  cancelTokenSource: null,
  fetchData: null,
  isFetching: false,
  key: null,
  previousRequest: null,
  receiveData: null,
  reset: false,
  sortBy: null,
  sortDirection: null,
};

export const defaultState = {
  //
};

/**
 * Fetches New Rows of Data
 * @function getData
 * @param {Number} size
 * @param {String} key
 * @return {Function} receiveData
 */
export const getData = (key, size) => {
  return async (dispatch, getState) => {
    /* Check that table data at this key exists */
    if (isFalsy(getState().table[key])) {
      console.warn('No table with this key exists.');
      return null;
    }

    const {
      [key]: {
        cancelTokenSource,
        fetchData,
        isFetching,
        previousRequest,
        sortBy,
        sortDirection,
      } } = getState().table;

    /* If we are currently fetching, do not make additional fetch. Prevents excessive getRows calls */
    if (isFetching) return null;
    /* If we have begun a new request, cancel the previous request to prevent outdated data */
    if (previousRequest) cancelTokenSource.cancel('CANCEL_DATA_REQUEST');

    /* Set Fetching */
    dispatch(isTableFetching(key, true));
    /* Create a new cancel token in case we receive another call before this is complete */
    const cancelToken = CancelToken.source();
    dispatch(setCancelTokenSource(key, cancelToken));
    /* Run the fetchData callback */
    const currentRequest = await fetchData({ cancelToken, size, sortBy, sortDirection });

    /* Store the previous request */
    dispatch(setPreviousRequest(key, currentRequest));
    /* Set table cache reset to false if it was previously set to true */
    dispatch(resetTableCache(key, false));
    /* Toggle off fetching flag */
    return dispatch(isTableFetching(key, false));
  };
};

export const IS_TABLE_FETCHING = 'IS_TABLE_FETCHING';
/**
 * Toggles Table Fetching
 * @function isTableFetching
 * @param {String} key
 * @param {boolean} bool
 * @return {Object} IS_TABLE_FETCHING Action Payload
 */
export const isTableFetching = (key, bool) => ({
  type: IS_TABLE_FETCHING,
  payload: {
    key,
    bool,
  },
});

export const REGISTER_TABLE = 'REGISTER_TABLE';
/**
 * Registers a new Table in State
 * @function registerTable
 * @param {Object} data - Table Data
 * @return {(Function | null)} Dispatch of REGISTER_TABLE Payload
 */
export const registerTable = data => {
  return (dispatch, getState) => {
    /* Provide warnings if some issues are occurring with registering the <Table /> */
    if (isFalsy(data)) {
      console.warn('No table data found. Please check that you are passing data to registerTable correctly.');
      return null;
    }
    if (isFalsy(data.key)) {
      console.warn('No table key found. Please check that you are passing a key to registerTable correctly.');
      return null;
    }
    if (isFalsy(data.fetchData)) {
      console.warn('FetchData function not found. Please check that you are passing it to registerTable correctly.');
      return null;
    }

    const doesTableExist = isTruthy(getState().table[data.key]);
    /* Provide warning that developer is overwriting an existing payload */
    if (doesTableExist) console.warn('A table at this key already exists. Overwriting the existing table.');
    return dispatch({
      type: REGISTER_TABLE,
      payload: data,
    });
  };
};

export const RESET_TABLE = 'RESET_TABLE';
/**
 * Resets the Current Table at the Given Key
 * @description Exposed Method to be Used at Route Level to Reset the Table
 * @function resetTable
 * @param {String} key
 * @return {Object} RESET_TABLE Action Payload
 */
export const resetTable = key => ({
  type: RESET_TABLE,
  payload: key,
});

export const RESET_TABLE_CACHE = 'RESET_TABLE_CACHE';
/**
 * Triggers `react-virtualized` Resetting its Row Cache
 * @function resetTableCache
 * @param {String} key
 * @param {boolean} bool
 * @return {Object} RESET_TABLE_CACHE Action Payload
 */
export const resetTableCache = (key, bool) => ({
  type: RESET_TABLE_CACHE,
  payload: {
    key,
    bool,
  },
});

export const SET_CANCEL_TOKEN_SOURCE = 'SET_CANCEL_TOKEN_SOURCE';
/**
 * Sets the Appropriate Axios Cancel Token Value
 * @function setCancelTokenSource
 * @param {String} key
 * @param {Object} source
 * @return {Object} SET_CANCEL_TOKEN_SOURCE Action Payload
 */
export const setCancelTokenSource = (key, source) => ({
  type: SET_CANCEL_TOKEN_SOURCE,
  payload: {
    key,
    source,
  },
});

export const SET_PREVIOUS_REQUEST = 'SET_PREVIOUS_REQUEST';
/**
 * Stores the Previous Request To Cancel if Necessary
 * @function setPreviousRequest
 * @param {String} key
 * @param {Object} request - Request Payload
 * @return {Object} SET_PREVIOUS_REQUEST Action Payload
 */
export const setPreviousRequest = (key, request) => ({
  type: SET_PREVIOUS_REQUEST,
  payload: {
    key,
    request,
  },
});

export const UPDATE_SORT = 'TABLE_UPDATE_SORT';
/**
 * Updates Table Sorting Data & Resets Table
 * @function updateSort
 * @param {Object} sortData
 * @param {String} key
 * @param {String} sortBy
 * @param {String} sortDirection
 * @return {Object} UPDATE_SORT Action Payload
 */
export const updateSort = sortData => ({
  type: UPDATE_SORT,
  payload: sortData,
});

export const UNREGISTER_TABLE = 'UNREGISTER_TABLE';
/**
 * Unregisters a Currently Active Table
 * @function unregisterTable
 * @param {String} key
 * @return {Object} UNREGISTER_TABLE Action Payload
 */
export const unregisterTable = key => ({
  type: UNREGISTER_TABLE,
  payload: key,
});

export default function reducer(state = defaultState, action = {}) {
  switch (action.type) {
    case IS_TABLE_FETCHING:
      return {
        ...state,
        [action.payload.key]: { ...state[action.payload.key], isFetching: action.payload.bool },
      };
    case REGISTER_TABLE:
      return {
        ...state,
        [action.payload.key]: { ...tableStateTemplate, ...action.payload },
      };
    case RESET_TABLE:
      return {
        ...state,
        [action.payload]: { ...state[action.payload], isFetching: false, reset: true },
      };
    case RESET_TABLE_CACHE:
      return {
        ...state,
        [action.payload.key]: { ...state[action.payload.key], reset: action.payload.bool },
      };
    case SET_CANCEL_TOKEN_SOURCE:
      return {
        ...state,
        [action.payload.key]: { ...state[action.payload.key], cancelTokenSource: action.payload.source },
      };
    case SET_PREVIOUS_REQUEST:
      return {
        ...state,
        [action.payload.key]: { ...state[action.payload.key], previousRequest: action.payload.request },
      };
    case UPDATE_SORT: {
      return {
        ...state,
        [action.payload.key]: {
          ...state[action.payload.key],
          isFetching: false,
          reset: true,
          sortBy: action.payload.sortBy,
          sortDirection: action.payload.sortDirection,
        },
      };
    }
    case UNREGISTER_TABLE:
      return {
        ...state,
        [action.payload]: {},
      };
    default:
      return state;
  }
}
